/** Tiny in-memory request cache + dedup.
 *
 * Purpose: stop the master-data refetch storm. Backend already caches
 * these on its end (TTL 5 min); we mirror the same TTL on the client so
 * navigating between pages feels instant.
 *
 * Surface:
 *   getCached(key)            – read; returns undefined if stale or missing
 *   setCached(key, data, ttl) – write
 *   invalidate(prefix)        – evict any key starting with `prefix`
 *   clearAll()                – nuke (used on logout)
 *   getInflight(key)          – returns Promise if a request for this key
 *                               is already in-flight (dedup)
 *   setInflight(key, promise) – register a promise; clears itself when it
 *                               resolves or rejects.
 *
 * Cache is process-local. We're a single SPA tab so that's fine.
 */

// { key → { data, expiresAt } }
const store = new Map();
// { key → Promise } — in-flight requests for dedup
const inflight = new Map();

export function getCached(key) {
  const item = store.get(key);
  if (!item) return undefined;
  if (item.expiresAt < Date.now()) {
    store.delete(key);
    return undefined;
  }
  return item.data;
}

export function setCached(key, data, ttl = 300_000) {
  store.set(key, { data, expiresAt: Date.now() + ttl });
}

export function invalidate(prefix) {
  let n = 0;
  for (const k of Array.from(store.keys())) {
    if (k.startsWith(prefix)) {
      store.delete(k);
      n += 1;
    }
  }
  return n;
}

export function clearAll() {
  store.clear();
  inflight.clear();
}

export function getInflight(key) {
  return inflight.get(key);
}

export function setInflight(key, promise) {
  inflight.set(key, promise);
  const cleanup = () => inflight.delete(key);
  promise.then(cleanup, cleanup);
  return promise;
}
